import { useTranslation } from 'react-i18next';
import { Check, Icon } from '../ui/Icon';
import Spinner from '../ui/Spinner';
import type { TripAutoSuggestPhase, TripAutoSuggestError, GeneratedDay } from '../../hooks/useTripAutoSuggest';

interface Props {
  phase: TripAutoSuggestPhase;
  destination: string;
  nights: number;
  venueNames: string[];
  days: GeneratedDay[];
  tipCount: number;
  ticketNames?: string[];
  unplacedTickets?: number;
  error: TripAutoSuggestError | null;
  onDone: () => void;
  // Import doesn't run a tips pass (the document defines the trip), so the step
  // is hidden rather than shown ticked-but-empty.
  showTips?: boolean;
  /** The wizard's bookings step had files. */
  showTickets?: boolean;
  /** Places and itinerary come from the AI or an imported plan; tickets alone skip them. */
  showSuggestions?: boolean;
}

type StepStatus = 'pending' | 'active' | 'done';

const STEP_ORDER: TripAutoSuggestPhase[] = ['bookings', 'places', 'itinerary', 'tips'];

// Only the tail is shown while a step runs: the list is a live feed, not a
// record, and an unbounded one would push the button out of the modal.
const VISIBLE_FEED_ITEMS = 4;

function stepStatus(step: TripAutoSuggestPhase, phase: TripAutoSuggestPhase): StepStatus {
  if (phase === 'done') return 'done';
  const current = STEP_ORDER.indexOf(phase);
  const own = STEP_ORDER.indexOf(step);
  // On error the phase isn't in STEP_ORDER, so nothing reads as active and
  // whatever already finished keeps its tick.
  if (current < 0) return 'pending';
  if (own < current) return 'done';
  return own === current ? 'active' : 'pending';
}

export default function TripGenerationOverlay({
  phase, destination, nights, venueNames, days, tipCount, ticketNames = [], unplacedTickets = 0, error, onDone,
  showTips = true, showTickets = false, showSuggestions = true,
}: Props) {
  const { t } = useTranslation();

  const finished = phase === 'done' || phase === 'error';
  const ticketsStatus = stepStatus('bookings', phase);
  const placesStatus = stepStatus('places', phase);
  const itineraryStatus = stepStatus('itinerary', phase);
  const tipsStatus = stepStatus('tips', phase);

  const errorText = phase === 'error'
    ? (error === 'insufficient_credits'
      ? t('dashboard.newTripWizard.generating.errorCredits')
      : t('dashboard.newTripWizard.generating.errorUnknown'))
    : null;

  // No venues means the itinerary pass never ran — ticking it would be a lie.
  const itinerarySkipped = phase === 'done' && venueNames.length === 0;
  const k = 'dashboard.newTripWizard.generating';

  return (
    <div className="space-y-5">
      <div>
        <div className="rounded-2xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800 [&>*]:px-4 [&>*]:py-3.5">
          {showTickets && (
            <Step
              status={ticketsStatus}
              label={t(`${k}.stepTickets`)}
              detail={ticketNames.length > 0 ? t(`${k}.stepTicketsDone`, { count: ticketNames.length }) : null}
              feed={ticketsStatus === 'active' ? ticketNames : []}
            />
          )}
          {showSuggestions && (
            <Step
              status={placesStatus}
              label={t('dashboard.newTripWizard.generating.stepPlaces', { destination })}
              detail={venueNames.length > 0 ? t('dashboard.newTripWizard.generating.stepPlacesFound', { count: venueNames.length }) : null}
              feed={placesStatus === 'active' ? venueNames : []}
            />
          )}
          {showSuggestions && !itinerarySkipped && (
            <Step
              status={itineraryStatus}
              label={t('dashboard.newTripWizard.generating.stepItinerary', { count: nights })}
              detail={days.length > 0 ? t('dashboard.newTripWizard.generating.stepItineraryDone', { count: days.length }) : null}
              feed={itineraryStatus === 'active'
                ? days.map((day, i) => t('dashboard.newTripWizard.generating.dayStops', {
                  number: day.dayNumber ?? i + 1,
                  count: day.stops,
                }))
                : []}
            />
          )}
          {showTips && (
            <Step
              status={tipsStatus}
              label={t('dashboard.newTripWizard.generating.stepTips', { destination })}
              detail={tipCount > 0 ? t('dashboard.newTripWizard.generating.stepTipsDone', { count: tipCount }) : null}
              feed={[]}
            />
          )}
        </div>
        {errorText && <p className="text-[13px] text-amber-600 dark:text-amber-400 pt-3">{errorText}</p>}
        {unplacedTickets > 0 && (
          <p className="text-[13px] text-amber-600 dark:text-amber-400 pt-3">{t(`${k}.ticketsUnplaced`, { count: unplacedTickets })}</p>
        )}
        {showSuggestions && itinerarySkipped && (
          <p className="text-[13px] text-slate-500 dark:text-slate-400 pt-3">
            {t('dashboard.newTripWizard.generating.doneEmpty')}
          </p>
        )}
      </div>

      {finished && (
        <button type="button" className="btn-primary w-full !h-12 justify-center" onClick={onDone}>
          {t('dashboard.newTripWizard.generating.cta')}
        </button>
      )}
    </div>
  );
}

function Step({ status, label, detail, feed }: {
  status: StepStatus;
  label: string;
  detail: string | null;
  feed: string[];
}) {
  return (
    <div>
      <div className="flex items-center gap-3 text-[14px] leading-relaxed">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
          status === 'done' ? 'bg-teal-500 text-white' : status === 'active' ? 'bg-teal-50 dark:bg-teal-900/30' : 'bg-slate-100 dark:bg-slate-800'
        }`}>
          {status === 'done' && <Icon as={Check} size={14} />}
          {status === 'active' && <Spinner size="sm" />}
          {status === 'pending' && <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />}
        </span>
        <span className={status === 'pending'
          ? 'text-slate-400 dark:text-slate-500'
          : 'text-slate-700 dark:text-slate-300 font-medium'}>
          {label}
        </span>
        {detail && <span className="text-[12px] text-slate-400 dark:text-slate-500 ml-auto shrink-0">{detail}</span>}
      </div>
      {feed.length > 0 && (
        <ul className="pl-10 pt-1.5 space-y-0.5">
          {feed.slice(-VISIBLE_FEED_ITEMS).map((item, i) => (
            <li key={`${item}-${i}`} className="flex items-center gap-1.5 text-[12px] text-slate-500 dark:text-slate-400">
              <Icon as={Check} size={11} className="text-teal-500/70 shrink-0" />
              <span className="truncate">{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
